import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Logo } from './Logo';
import { useI18n } from '../i18n/I18nProvider';
import { NAV } from '../constants/testIds';

const links = [
  { to: '/heritage', key: 'heritage', tid: NAV.heritage },
  { to: '/collections', key: 'collections', tid: NAV.collections },
  { to: '/boutique', key: 'boutique', tid: NAV.boutique },
  { to: '/b2b', key: 'b2b', tid: NAV.b2b },
  { to: '/contact', key: 'contact', tid: NAV.contact },
];

export function Header() {
  const { lang, setLang, t } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const onHomeHero = location.pathname === '/' && !scrolled;
  const textColor = onHomeHero ? 'text-white' : 'text-ink';
  const borderColor = onHomeHero ? 'border-white/10' : 'border-sand/60';
  const bgClass = onHomeHero
    ? 'bg-transparent'
    : 'bg-pure/85 backdrop-blur-md';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${bgClass} border-b ${borderColor}`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid={NAV.logo} className="flex items-center">
          <Logo light={onHomeHero} />
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.key}
              to={l.to}
              data-testid={l.tid}
              className={({ isActive }) =>
                `link-underline text-[0.78rem] uppercase tracking-[0.18em] font-medium ${textColor} ${
                  isActive ? 'opacity-100' : 'opacity-80 hover:opacity-100'
                }`
              }
            >
              {t(`nav.${l.key}`)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-5">
          <button
            data-testid={NAV.langToggle}
            onClick={() => setLang(lang === 'fr' ? 'en' : 'fr')}
            className={`text-[0.72rem] uppercase tracking-[0.22em] font-medium ${textColor} opacity-80 hover:opacity-100 transition-opacity`}
            aria-label="Toggle language"
          >
            <span className={lang === 'fr' ? 'font-semibold' : 'opacity-60'}>FR</span>
            <span className="mx-1.5 opacity-40">/</span>
            <span className={lang === 'en' ? 'font-semibold' : 'opacity-60'}>EN</span>
          </button>

          <button
            data-testid={open ? NAV.mobileMenuClose : NAV.mobileMenuOpen}
            onClick={() => setOpen((v) => !v)}
            className={`lg:hidden ${textColor}`}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-pure border-t border-sand/60">
          <nav className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <NavLink
                key={l.key}
                to={l.to}
                data-testid={`${l.tid}-mobile`}
                className="text-base font-serif text-ink"
              >
                {t(`nav.${l.key}`)}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
